import React, { useState, useEffect } from 'react';
import { getFirestore, doc, setDoc, onSnapshot, getDoc, serverTimestamp, collection, addDoc, query, orderBy, deleteDoc, updateDoc } from 'firebase/firestore';
import { Plus, XCircle, Zap, Edit, Trash2 } from 'lucide-react';
import { db } from './firebase';
import { apiCall, getErrorMessage } from './utils/api';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- SHARED COMPONENTS (palette-only: #3C0F6C, #fafafa, #d5e7f3) ---
const Input = ({ label, ...props }) => (
  <div className="space-y-2 w-full">
    {label && <label className="text-sm font-medium text-[#3C0F6C]/80">{label}</label>}
    <input
      {...props}
      className="w-full px-4 py-3 bg-[#fafafa] border border-[#d5e7f3] text-[#3C0F6C] rounded-xl focus:ring-2 focus:ring-[#3C0F6C] focus:border-[#3C0F6C] transition-shadow placeholder:text-[#3C0F6C]/50"
    />
  </div>
);

const TextArea = ({ label, ...props }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-[#3C0F6C]/80">{label}</label>
    <textarea
      {...props}
      rows="4"
      className="w-full px-4 py-3 bg-[#fafafa] border border-[#d5e7f3] text-[#3C0F6C] rounded-xl focus:ring-2 focus:ring-[#3C0F6C] focus:border-[#3C0F6C] transition-shadow placeholder:text-[#3C0F6C]/50"
    ></textarea>
  </div>
);

// Helper to convert resume data object to a string, used by multiple components
const formatResumeDataAsText = (data) => {
  if (!data || !data.personalInfo) return "";
  let text = `Name: ${data.personalInfo.name}\nEmail: ${data.personalInfo.email}\nPhone: ${data.personalInfo.phone}\nLocation: ${data.personalInfo.location}\n\n`;
  if (data.goal) text += `## Goal\n${data.goal}\n\n`;
  text += "## Work Experience\n";
  (data.experience || []).forEach(exp => {
    text += `- ${exp.title} at ${exp.company} (${exp.period})\n  ${exp.description}\n`;
  });
  text += "\n## Education\n";
  (data.education || []).forEach(edu => {
    text += `- ${edu.degree} from ${edu.school} (${edu.period})\n  ${edu.description}\n`;
  });
  text += "\n## Projects\n";
  (data.projects || []).forEach(proj => {
    text += `- ${proj.title} (${proj.period})\n  ${proj.description}\n`;
  });
  text += "\n## Skills\n";
  {
    const skills = Array.isArray(data.skills)
      ? data.skills.filter(Boolean).map(String)
      : (typeof data.skills === 'string'
        ? data.skills.split(',').map(s => s.trim()).filter(Boolean)
        : []);
    text += skills.join(', ');
  }
  text += "\n\n## Achievements\n";
  (data.achievements || []).forEach(ach => {
    text += `- ${ach.description}\n`;
  });
  text += "\n## Awards\n";
  (data.awards || []).forEach(award => {
    text += `- ${award.description}\n`;
  });
  text += "\n## Certificates\n";
  (data.certificates || []).forEach(cert => {
    text += `- ${cert.description}\n`;
  });
  text += "\n## Hobbies\n";
  (data.hobbies || []).forEach(hobby => {
    text += `- ${hobby.description}\n`;
  });
  return text;
};

const CoverLetterGenerator = ({ user, resumeData, initialJobDescription, onClearInitialJd, appId }) => {
  const [isGeneratorModalOpen, setIsGeneratorModalOpen] = useState(false);
  const [viewingLetter, setViewingLetter] = useState(null);
  const [coverLetters, setCoverLetters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialJobDescription) {
      setIsGeneratorModalOpen(true);
    }
  }, [initialJobDescription]);

  const handleModalClose = () => {
    setIsGeneratorModalOpen(false);
    if (initialJobDescription && onClearInitialJd) {
      onClearInitialJd();
    }
  };

  useEffect(() => {
    if (user) {
      const q = query(collection(db, `artifacts/${appId}/users/${user.uid}/cover_letters`), orderBy('createdAt', 'desc'));
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const letters = [];
        querySnapshot.forEach((doc) => {
          letters.push({ id: doc.id, ...doc.data() });
        });
        setCoverLetters(letters);
        setIsLoading(false);
      }, (err) => {
        console.error('Error fetching cover letters:', err);
        setError('Could not fetch cover letters.');
        setIsLoading(false);
      });
      return () => unsubscribe();
    }
  }, [user, db, appId]);

  const handleGenerateLetter = async ({ jobDescriptionText, jobDescriptionFile, jdInputType }) => {
    const canGenerate = (jdInputType === 'text' && jobDescriptionText) || (jdInputType === 'file' && jobDescriptionFile);
    if (!canGenerate || !user) return;

    const formData = new FormData();
    formData.append('resume_text', formatResumeDataAsText(resumeData));

    let jobTitle = 'Cover Letter';
    if (jdInputType === 'text') {
      formData.append('job_description_text', jobDescriptionText);
      jobTitle = jobDescriptionText.split('\n')[0].substring(0, 50) || 'Cover Letter';
    } else {
      formData.append('job_description_file', jobDescriptionFile);
      jobTitle = jobDescriptionFile.name;
    }

    try {
      const { data: result } = await apiCall('/api/generate-cover-letter', {
        method: 'POST',
        body: formData,
      });

      const coverLettersCol = collection(db, `artifacts/${appId}/users/${user.uid}/cover_letters`);
      const newDocRef = await addDoc(coverLettersCol, {
        title: jobTitle,
        letter: result.cover_letter,
        jobDescription: jdInputType === 'text' ? jobDescriptionText : 'File upload',
        createdAt: serverTimestamp(),
      });

      handleModalClose();
      setViewingLetter({ id: newDocRef.id, title: jobTitle, letter: result.cover_letter });

    } catch (err) {
      console.error('Error generating cover letter:', err);
      throw err;
    }
  };

  const handleDeleteLetter = async (letterId) => {
    if (window.confirm('Are you sure you want to delete this cover letter?')) {
      const letterDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/cover_letters`, letterId);
      try {
        await deleteDoc(letterDocRef);
      } catch (err) {
        console.error('Error deleting cover letter:', err);
        setError('Could not delete the cover letter.');
      }
    }
  };

  const handleUpdateLetter = async (letterId, newContent) => {
    const letterDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/cover_letters`, letterId);
    try {
      await updateDoc(letterDocRef, {
        letter: newContent
      });
    } catch (err) {
      console.error('Error updating cover letter:', err);
      setError('Could not save changes to the cover letter.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <svg className="animate-spin h-8 w-8 text-[#3C0F6C]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
      </div>
    );
  }

  return (
    <div>
      <header className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-black-500">AI Cover Letter Generator</h2>
        <button onClick={() => setIsGeneratorModalOpen(true)} className="bg-[#3C0F6C] text-[#fafafa] font-semibold py-2 px-4 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors flex items-center gap-2">
          <Plus size={18} />
          <span>Create New</span>
        </button>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <CreateNewCard onClick={() => setIsGeneratorModalOpen(true)} />
        {coverLetters.map(letter => (
          <CoverLetterCard key={letter.id} letter={letter} onView={() => setViewingLetter(letter)} onDelete={() => handleDeleteLetter(letter.id)} />
        ))}
      </div>

      {isGeneratorModalOpen && (
        <GeneratorModal
          isOpen={isGeneratorModalOpen}
          onClose={handleModalClose}
          onGenerate={handleGenerateLetter}
          initialJobDescription={initialJobDescription}
        />
      )}

      {viewingLetter && (
        <ViewLetterModal
          letter={viewingLetter}
          onClose={() => setViewingLetter(null)}
          onSave={handleUpdateLetter}
        />
      )}
    </div>
  );
};

const GeneratorModal = ({ isOpen, onClose, onGenerate, initialJobDescription }) => {
  const [jdInputType, setJdInputType] = useState('text');
  const [jobDescriptionText, setJobDescriptionText] = useState('');
  const [jobDescriptionFile, setJobDescriptionFile] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      if (initialJobDescription) {
        setJobDescriptionText(initialJobDescription);
        setJdInputType('text');
      } else {
        setJobDescriptionText('');
      }
    }
  }, [isOpen, initialJobDescription]);

  const handleGenerateClick = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      await onGenerate({ jobDescriptionText, jobDescriptionFile, jdInputType });
    } catch (e) {
      setError(e.message);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#3C0F6C]/70 flex items-center justify-center p-4 z-50">
      <div className="bg-[#fafafa] border border-[#d5e7f3] rounded-2xl shadow-2xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-[#3C0F6C]">New Cover Letter</h3>
            <button onClick={onClose} className="p-1 rounded-full text-[#3C0F6C] hover:bg-[#d5e7f3]"><XCircle size={20} /></button>
          </div>
          {error && <p className="text-sm text-[#3C0F6C] bg-[#d5e7f3] p-3 rounded-xl mb-4">{error}</p>}
          <div className="mb-4 border-b border-[#d5e7f3]">
            <nav className="-mb-px flex space-x-6">
              <button onClick={() => setJdInputType('text')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${jdInputType === 'text' ? 'border-[#3C0F6C] text-[#3C0F6C]' : 'border-transparent text-[#3C0F6C]/70 hover:text-[#3C0F6C] hover:border-[#d5e7f3]'}`}>Paste Text</button>
              <button onClick={() => setJdInputType('file')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${jdInputType === 'file' ? 'border-[#3C0F6C] text-[#3C0F6C]' : 'border-transparent text-[#3C0F6C]/70 hover:text-[#3C0F6C] hover:border-[#d5e7f3]'}`}>Upload File</button>
            </nav>
          </div>
          {jdInputType === 'text' ? (
            <TextArea label="Job Description Text" placeholder="Paste the job description here..." value={jobDescriptionText} onChange={e => setJobDescriptionText(e.target.value)} />
          ) : (
            <div>
              <label className="text-sm font-medium text-[#3C0F6C]/80 block mb-1">Job Description File</label>
              <input type="file" onChange={(e) => setJobDescriptionFile(e.target.files[0])} accept=".pdf,.doc,.docx" className="block w-full text-sm text-[#3C0F6C] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border file:border-[#d5e7f3] file:text-sm file:font-semibold file:bg-[#d5e7f3] file:text-[#3C0F6C] hover:file:bg-[#fafafa]" />
            </div>
          )}
        </div>
        <div className="p-4 bg-[#d5e7f3]/40 text-right rounded-b-2xl border-t border-[#d5e7f3]">
          <button onClick={handleGenerateClick} disabled={isGenerating} className="bg-[#3C0F6C] text-[#fafafa] font-semibold py-2 px-6 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {isGenerating ? <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> : <Zap size={18} />}
            <span>{isGenerating ? 'Generating...' : 'Generate & Save'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const ViewLetterModal = ({ letter, onClose, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(letter.letter);

  const handleSave = () => {
    onSave(letter.id, editedContent);
    setIsEditing(false);
  };

  if (!letter) return null;

  return (
    <div className="fixed inset-0 bg-[#3C0F6C]/70 flex items-center justify-center p-4 z-50">
      <div className="bg-[#fafafa] border border-[#d5e7f3] rounded-2xl shadow-2xl max-w-2xl w-full flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-[#d5e7f3] flex-shrink-0 flex justify-between items-center">
          <h3 className="text-xl font-bold text-[#3C0F6C]">{letter.title}</h3>
          {!isEditing && (
            <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 text-sm bg-[#d5e7f3] text-[#3C0F6C] font-semibold py-1 px-3 rounded-xl hover:bg-[#fafafa] border border-[#d5e7f3]">
              <Edit size={14} />
              <span>Edit</span>
            </button>
          )}
        </div>
        <div className="p-6 overflow-y-auto">
          {isEditing ? (
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full h-96 bg-[#fafafa] border border-[#d5e7f3] text-[#3C0F6C] rounded-xl p-3 focus:ring-2 focus:ring-[#3C0F6C] focus:border-[#3C0F6C]"
            />
          ) : (
            <p className="text-[#3C0F6C] whitespace-pre-wrap">{letter.letter}</p>
          )}
        </div>
        <div className="p-4 bg-[#d5e7f3]/40 text-right rounded-b-2xl border-t border-[#d5e7f3]">
          {isEditing ? (
            <div className="flex justify-end gap-4">
              <button onClick={() => setIsEditing(false)} className="bg-[#d5e7f3] text-[#3C0F6C] font-semibold py-2 px-4 rounded-xl hover:bg-[#fafafa] border border-[#d5e7f3]">Cancel</button>
              <button onClick={handleSave} className="bg-[#3C0F6C] text-[#fafafa] font-semibold py-2 px-4 rounded-xl hover:bg-[#3C0F6C]/90">Save Changes</button>
            </div>
          ) : (
            <button onClick={onClose} className="bg-[#d5e7f3] text-[#3C0F6C] font-semibold py-2 px-4 rounded-xl hover:bg-[#fafafa] border border-[#d5e7f3]">Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

const CreateNewCard = ({ onClick }) => (
  <button onClick={onClick} className="aspect-[3/4] border-2 border-dashed border-[#d5e7f3] rounded-2xl flex flex-col items-center justify-center text-[#3C0F6C]/80 hover:bg-[#d5e7f3]/40 hover:border-[#3C0F6C] hover:text-[#3C0F6C] transition-colors">
    <Plus size={32} />
    <span className="mt-2 font-semibold">New Cover Letter</span>
  </button>
);

const CoverLetterCard = ({ letter, onView, onDelete }) => (
  <div onClick={onView} className="group relative aspect-[3/4] bg-[#fafafa] rounded-2xl shadow-sm border border-[#d5e7f3] p-4 flex flex-col justify-between hover:shadow transition-shadow cursor-pointer">
    <div className="flex justify-between items-start">
      <h3 className="font-bold text-[#3C0F6C] line-clamp-1 pr-2">{letter.title}</h3>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="p-1.5 rounded-full hover:bg-[#d5e7f3] text-[#3C0F6C] flex-shrink-0"
      >
        <Trash2 size={16} />
      </button>
    </div>
    <div>
      <p className="text-sm text-[#3C0F6C]/80 mt-2 line-clamp-4">{letter.letter}</p>
    </div>
    <div className="text-xs text-[#3C0F6C]/70 mt-4">
      {letter.createdAt ? new Date(letter.createdAt.seconds * 1000).toLocaleDateString() : ''}
    </div>
  </div>
);

export default CoverLetterGenerator;
